import { Link, useForm, usePage } from '@inertiajs/react'
import { Button } from '@/shared/ui/button'
import { Label } from '@/shared/ui/label'
import { PasswordInput } from '@/shared/ui/password-input'
import { PasswordPolicyHints } from '@/shared/ui/password-policy-hints'
import { t } from '@/shared/lib/i18n'
import { completeNewPasswordSessionPath, cancelNewPasswordSessionPath } from '@/shared/lib/routes'
import { AuthShell } from './AuthShell'

interface NewPasswordPageProps {
  [key: string]: unknown
  email: string
  errors?: Record<string, string[]>
  flash?: { alert?: string; notice?: string }
}

const NewPassword = () => {
  const { email, errors: serverErrors, flash } = usePage<NewPasswordPageProps>().props

  const form = useForm({ password: '', password_confirmation: '' })
  const formFieldErrors = form.errors as typeof form.errors & {
    base?: string
    passwordConfirmation?: string
  }
  const errorMessage = formFieldErrors.base ?? serverErrors?.base?.[0] ?? flash?.alert

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.post(completeNewPasswordSessionPath())
  }

  return (
    <AuthShell pageTitle={t('auth.newPassword.title')}>
      <h1 className="text-2xl font-bold">{t('auth.newPassword.title')}</h1>

      {errorMessage && (
        <div
          role="alert"
          className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {errorMessage}
        </div>
      )}

      <p className="text-sm text-muted-foreground">{t('auth.newPassword.emailLabel', { email })}</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="password">{t('auth.newPassword.passwordLabel')}</Label>
          <PasswordInput
            id="password"
            autoComplete="new-password"
            aria-describedby="password-hints"
            value={form.data.password}
            onChange={(e) => form.setData('password', e.target.value)}
            required
          />
          <PasswordPolicyHints />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password_confirmation">
            {t('auth.newPassword.passwordConfirmationLabel')}
          </Label>
          <PasswordInput
            id="password_confirmation"
            autoComplete="new-password"
            disablePaste
            value={form.data.password_confirmation}
            onChange={(e) => form.setData('password_confirmation', e.target.value)}
            required
          />
          {(formFieldErrors.passwordConfirmation || serverErrors?.passwordConfirmation?.[0]) && (
            <p className="text-sm text-destructive">
              {formFieldErrors.passwordConfirmation ?? serverErrors?.passwordConfirmation?.[0]}
            </p>
          )}
        </div>
        <Button type="submit" className="w-full" disabled={form.processing}>
          {form.processing ? t('auth.newPassword.submitting') : t('auth.newPassword.submit')}
        </Button>
        <div className="text-center">
          <Link
            href={cancelNewPasswordSessionPath()}
            method="delete"
            as="button"
            className="text-sm text-muted-foreground underline-offset-4 hover:underline"
          >
            {t('auth.newPassword.backToSignIn')}
          </Link>
        </div>
      </form>
    </AuthShell>
  )
}

export default NewPassword
